import { NextRouter, useRouter } from "next/router"
import { emailRegExp } from "@nova/util-strings"
import { isValidString } from "tools/strings/isNonEmptyString"
import { RegistrationFlow } from "./constants"

/**
 * Normalizes a query string parameter into an email
 * - checks if array and only takes the first element
 * - checks if it looks like an email
 * @param name of the query string
 * @param router to collect query strings from
 * @returns an email if it is present and valid; undefined otherwise
 */
function normalizeQueryEmail(name: string, router: NextRouter): string | undefined {
    const queryString = router.query[name]
    const email = !!queryString && Array.isArray(queryString) ? queryString[0] : queryString
    if (!!email && !isValidString(email, emailRegExp)) {
        // this is just some sanity checking before we go any further with the email
        console.warn(
            `Did not get a valid email for query ${name} - skipping customized registration flow`,
            email
        )
        return undefined
    }
    return email
}

/**
 * Normalizes a query string parameter into a flow
 * - checks if array and only takes the first element
 * - figures out which registration flow it looks like
 * @param name of the query string
 * @param router to collect query strings from
 * @returns which flow we should be using
 */
function normalizeQueryFlow(name: string, router: NextRouter): RegistrationFlow {
    const queryString = router.query[name]
    const flowName = !!queryString && Array.isArray(queryString) ? queryString[0] : queryString
    switch (flowName) {
        case RegistrationFlow.Paid:
            return RegistrationFlow.Paid
        case RegistrationFlow.Invited:
            return RegistrationFlow.Invited
        case RegistrationFlow.Returning:
            return RegistrationFlow.Returning
        default:
            return RegistrationFlow.Free
    }
}

/**
 * Hook to use any user information that has been included in the link, including specialized flow
 */
export const useLinkedUser = (): {
    linkedEmail?: string
    linkedFlow: RegistrationFlow
} => {
    const router = useRouter()

    return {
        linkedEmail: normalizeQueryEmail("email", router),
        linkedFlow: normalizeQueryFlow("flow", router),
    }
}
